import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { supabase } from '../../lib/supabase';
import SEOHead from '../../components/base/SEOHead';
import Navbar from '../home/components/Navbar';
import Footer from '../home/components/Footer';
import CartServices from './components/CartServices';
import HangersAndBags from './components/HangersAndBags';
import MembershipUpgrade from './components/MembershipUpgrade';
import { searchStreets, getZipForStreet } from '../../data/czech-streets';

// Online Czech address search API (covers ALL streets in Czech Republic)
const searchCzechAddressesOnline = async (query: string): Promise<
  { street: string; city: string; zip: string; fullAddress: string; source: string }[]
> => {
  try {
    const response = await fetch(
      `${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/czech-address-search`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ query }),
      }
    );
    if (!response.ok) return [];
    const data = await response.json();
    return data.results || [];
  } catch {
    return [];
  }
};

declare global {
  interface Window {
    google: any;
    initGooglePlaces: () => void;
  }
}

export default function CartPage() {
  const navigate = useNavigate();
  const { t } = useTranslation();

  // ==== Form fields ====
  const [customerName, setCustomerName] = useState('');
  const [customerEmail, setCustomerEmail] = useState('');
  const [customerPhone, setCustomerPhone] = useState('');
  const [deliveryStreet, setDeliveryStreet] = useState('');
  const [deliveryStreetNumber, setDeliveryStreetNumber] = useState('');
  const [deliveryCity, setDeliveryCity] = useState('');
  const [deliveryZip, setDeliveryZip] = useState('');
  const [message, setMessage] = useState('');
  const [pickupDate, setPickupDate] = useState('');
  const [pickupTimeSlot, setPickupTimeSlot] = useState('');
  const [paymentMethod, setPaymentMethod] = useState<'bank_transfer' | 'cash'>('bank_transfer');

  // ==== Cart ====
  const [cartItems, setCartItems] = useState<any[]>([]);
  const [detectedLocation, setDetectedLocation] = useState<string | null>(null);
  const [isExpressDelivery, setIsExpressDelivery] = useState(false);
  const [isWeekendPickup, setIsWeekendPickup] = useState(false);
  const [membershipNumber, setMembershipNumber] = useState('');
  const [membership, setMembership] = useState<any>(null);
  const [isVerifyingMembership, setIsVerifyingMembership] = useState(false);
  const [membershipError, setMembershipError] = useState('');

  // Membership code verification states
  const [membershipCode, setMembershipCode] = useState('');
  const [checkingMembership, setCheckingMembership] = useState(false);
  const [membershipDiscount, setMembershipDiscount] = useState(0);
  const [membershipStatus, setMembershipStatus] = useState<{
    type: 'success' | 'warning' | 'error';
    message: string;
  } | null>(null);

  // Pending upgrade (applied in cart, not yet purchased)
  const [pendingUpgrade, setPendingUpgrade] = useState<{
    planName: string;
    discount: number;
    limit: number;
    price: number;
  } | null>(null);

  // Store original membership before upgrade
  const [originalMembership, setOriginalMembership] = useState<{
    plan_name: string;
    discount: number;
    membership_code?: string;
  } | null>(null);

  const [hangerCount, setHangerCount] = useState(0);
  const [bagCount, setBagCount] = useState(0);
  const [useOwnHanger, setUseOwnHanger] = useState(false);
  const [useOwnBag, setUseOwnBag] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [showCalendar, setShowCalendar] = useState(false);
  const [calendarMonth, setCalendarMonth] = useState(new Date());
  const calendarRef = useRef<HTMLDivElement>(null);
  const [isGoogleLoaded, setIsGoogleLoaded] = useState(false);
  const [streetSuggestions, setStreetSuggestions] = useState<any[]>([]);
  const [localStreetSuggestions, setLocalStreetSuggestions] = useState<string[]>([]);
  const [onlineAddressSuggestions, setOnlineAddressSuggestions] = useState<{
    street: string;
    city: string;
    zip: string;
    fullAddress: string;
    source: string;
  }[]>([]);
  const [isSearchingAddress, setIsSearchingAddress] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const autocompleteServiceRef = useRef<any>(null);
  const placesServiceRef = useRef<any>(null);
  const suggestionsRef = useRef<HTMLDivElement>(null);
  const addressSearchTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Load cart from localStorage
  useEffect(() => {
    const savedCart = localStorage.getItem('quickCart');
    if (savedCart) {
      try {
        const cartData = JSON.parse(savedCart);
        if (cartData.items) setCartItems(cartData.items);
        if (cartData.location) setDetectedLocation(cartData.location);
      } catch (e) {
        console.error(e);
      }
    }

    // Auto-detect membership upgrade from localStorage (when returning from checkout)
    const savedUpgrade = localStorage.getItem('cartMembershipUpgrade');
    if (savedUpgrade) {
      try {
        const upgradeData = JSON.parse(savedUpgrade);
        if (upgradeData.planName && upgradeData.discount && upgradeData.limit) {
          setPendingUpgrade(upgradeData);
          setMembership({ plan_name: upgradeData.planName, discount: upgradeData.discount });
          setMembershipDiscount(upgradeData.discount);
          setMembershipStatus({
            type: 'success',
            message: `Členství ${upgradeData.planName} aplikováno v košíku. Sleva ${upgradeData.discount}% je aktivní.`,
          });
        }
      } catch (e) {
        console.error(e);
      }
    }
  }, []);

  // Load Google Places script
  useEffect(() => {
    if (window.google?.maps?.places) {
      setIsGoogleLoaded(true);
      return;
    }
    window.initGooglePlaces = () => setIsGoogleLoaded(true);
    const script = document.createElement('script');
    script.src = `https://maps.googleapis.com/maps/api/js?key=AIzaSyBFw0Qbyq9zTFTd-tUY6dZWTgaQzuU17R8&libraries=places&callback=initGooglePlaces`;
    script.async = true;
    script.defer = true;
    document.head.appendChild(script);
    return () => {
      window.initGooglePlaces = () => {};
    };
  }, []);

  // Initialise autocomplete services once Google is ready
  useEffect(() => {
    if (!isGoogleLoaded) return;
    try {
      autocompleteServiceRef.current = new window.google.maps.places.AutocompleteService();
      const dummyDiv = document.createElement('div');
      dummyDiv.style.display = 'none';
      document.body.appendChild(dummyDiv);
      placesServiceRef.current = new window.google.maps.places.PlacesService(dummyDiv);
      document.body.removeChild(dummyDiv);
    } catch (e) {
      console.error(e);
    }
  }, [isGoogleLoaded]);

  // Street suggestions (local + online API for ALL Czech streets)
  useEffect(() => {
    // 1. Okamžitě zobrazit lokální výsledky
    if (deliveryStreet.length >= 1) {
      const localResults = searchStreets(deliveryStreet, 5);
      setLocalStreetSuggestions(localResults);
      if (localResults.length > 0) setShowSuggestions(true);
    } else {
      setLocalStreetSuggestions([]);
      setOnlineAddressSuggestions([]);
      setShowSuggestions(false);
    }

    // 2. Online vyhledávání s debounce (VŠECHNY ulice z celé ČR)
    if (addressSearchTimeoutRef.current) {
      clearTimeout(addressSearchTimeoutRef.current);
    }

    if (deliveryStreet.length >= 2) {
      setIsSearchingAddress(true);
      addressSearchTimeoutRef.current = setTimeout(async () => {
        const results = await searchCzechAddressesOnline(deliveryStreet);
        setOnlineAddressSuggestions(results);
        if (results.length > 0) setShowSuggestions(true);
        setIsSearchingAddress(false);
      }, 0);
    } else {
      setOnlineAddressSuggestions([]);
      setIsSearchingAddress(false);
    }

    // 3. Google Places jako záloha
    if (isGoogleLoaded && autocompleteServiceRef.current && deliveryStreet.length >= 3) {
      autocompleteServiceRef.current.getPlacePredictions(
        {
          input: deliveryStreet,
          types: ['address'],
          componentRestrictions: { country: 'cz' },
        },
        (predictions: any[], status: string) => {
          if (status === window.google.maps.places.PlacesServiceStatus.OK && predictions) {
            setStreetSuggestions(predictions.slice(0, 3));
          } else {
            setStreetSuggestions([]);
          }
        }
      );
    } else {
      setStreetSuggestions([]);
    }

    return () => {
      if (addressSearchTimeoutRef.current) {
        clearTimeout(addressSearchTimeoutRef.current);
      }
    };
  }, [deliveryStreet, isGoogleLoaded]);

  const handleSelectLocalSuggestion = (streetName: string) => {
    setDeliveryStreet(streetName);
    setShowSuggestions(false);
    setLocalStreetSuggestions([]);
    setStreetSuggestions([]);
    setOnlineAddressSuggestions([]);
    const zipInfo = getZipForStreet(streetName, deliveryCity);
    if (zipInfo) {
      setDeliveryZip(zipInfo.zip);
      setDeliveryCity(zipInfo.city);
    }
  };

  const handleSelectOnlineSuggestion = (suggestion: {
    street: string;
    city: string;
    zip: string;
    fullAddress: string;
    source: string;
  }) => {
    setDeliveryStreet(suggestion.street);
    if (suggestion.city) setDeliveryCity(suggestion.city);
    if (suggestion.zip) setDeliveryZip(suggestion.zip);
    setShowSuggestions(false);
    setLocalStreetSuggestions([]);
    setStreetSuggestions([]);
    setOnlineAddressSuggestions([]);
  };

  const handleSelectSuggestion = (suggestion: any) => {
    if (!placesServiceRef.current) return;
    placesServiceRef.current.getDetails(
      { placeId: suggestion.place_id, fields: ['address_components'] },
      (place: any, status: string) => {
        if (status === window.google.maps.places.PlacesServiceStatus.OK && place) {
          let street = '',
            city = '',
            zip = '';
          for (const c of place.address_components) {
            if (c.types.includes('route')) street = c.long_name;
            if (c.types.includes('locality') || c.types.includes('sublocality_level_1')) city = c.long_name;
            if (c.types.includes('postal_code')) zip = c.long_name;
          }
          setDeliveryStreet(street);
          if (city) setDeliveryCity(city);
          if (zip) setDeliveryZip(zip);
        }
        setShowSuggestions(false);
        setStreetSuggestions([]);
      }
    );
  };

  // Pricing helpers
  const getMembershipPiecesLimit = (): number => {
    // Pending upgrade has priority
    if (pendingUpgrade) return pendingUpgrade.limit;
    if (membership?.plan_name === 'VIP' || membershipDiscount === 30) return 30;
    if (membership?.plan_name === 'PREMIUM' || membershipDiscount === 20) return 20;
    if (membership?.plan_name === 'BASIC' || membershipDiscount === 15) return 10;
    return 0;
  };

  const getDiscountRate = () => {
    if (pendingUpgrade) return pendingUpgrade.discount / 100;
    if (membershipDiscount > 0) return membershipDiscount / 100;
    if (!membership) return 0;
    if (membership.plan_name === 'VIP') return 0.3;
    if (membership.plan_name === 'PREMIUM') return 0.2;
    return 0.15;
  };

  const getDiscountPercent = () => Math.round(getDiscountRate() * 100);
  const getItemPrice = (basePrice: number) => Math.round(basePrice * (1 - getDiscountRate()));

  // Helper: total items in cart
  const getTotalItems = (): number => cartItems.reduce((sum, item) => sum + item.quantity, 0);

  // Helper: how many discounted pieces remain for display purposes
  const getDiscountedPiecesUsed = (): number => {
    const limit = getMembershipPiecesLimit();
    const totalItems = getTotalItems();
    return Math.min(limit, totalItems);
  };

  const getOverLimitPieces = (): number => {
    const limit = getMembershipPiecesLimit();
    if (limit === 0) return 0;
    const totalItems = getTotalItems();
    return Math.max(0, totalItems - limit);
  };

  // Přidání chybějících funkcí pro výpočet cen
  const getOriginalSubtotal = () => {
    return cartItems.reduce((sum, item) => sum + item.basePrice * item.quantity, 0);
  };

  const getSubtotal = () => {
    const limit = getMembershipPiecesLimit();
    if (limit === 0 || (!membership && membershipDiscount === 0)) {
      // Bez členství - plná cena
      return getOriginalSubtotal();
    }

    let total = 0;
    let itemsProcessed = 0;

    for (const item of cartItems) {
      for (let i = 0; i < item.quantity; i++) {
        itemsProcessed++;
        if (itemsProcessed <= limit) {
          // Kus se slevou
          total += getItemPrice(item.basePrice);
        } else {
          // Kus za plnou cenu
          total += item.basePrice;
        }
      }
    }

    return total;
  };

  const getDiscountAmount = () => {
    const limit = getMembershipPiecesLimit();
    if (limit === 0 || (!membership && membershipDiscount === 0)) return 0;

    let discountSum = 0;
    let itemsProcessed = 0;

    for (const item of cartItems) {
      for (let i = 0; i < item.quantity; i++) {
        itemsProcessed++;
        if (itemsProcessed <= limit) {
          // Sleva na tento kus
          discountSum += item.basePrice - getItemPrice(item.basePrice);
        }
      }
    }

    return discountSum;
  };

  const getHangerCost = () => (useOwnHanger ? 0 : hangerCount * 5);
  const getBagCost = () => (useOwnBag ? 0 : bagCount * 15);
  const getShippingCost = () => {
    // VIP členství má dopravu ZDARMA
    if (isVipMember()) return 0;
    // BASIC, PREMIUM i bez členství platí 399 Kč
    return 399;
  };

  // VIP benefits check
  const isVipMember = () => {
    if (pendingUpgrade?.planName === 'VIP') return true;
    return membership?.plan_name === 'VIP' || membershipDiscount === 30;
  };
  const isPremiumOrVip = () => {
    if (pendingUpgrade?.planName === 'VIP' || pendingUpgrade?.planName === 'PREMIUM') return true;
    return membership?.plan_name === 'VIP' || membership?.plan_name === 'PREMIUM' || membershipDiscount >= 20;
  };

  const getExpressCost = () => (isVipMember() ? 0 : 399);
  const getWeekendCost = () => (isVipMember() ? 0 : 299);

  const getUpgradePrice = () => {
    return pendingUpgrade?.price || 0;
  };

  const getTotalPrice = () => {
    let total = getSubtotal() + getHangerCost() + getBagCost() + getShippingCost() + getUpgradePrice();
    if (isExpressDelivery && !isVipMember()) total += 399;
    if (isWeekendPickup && !isVipMember()) total += 299;
    return total;
  };

  // Time slots handling
  const timeSlots = [
    { value: '08:00-10:00', label: '08:00 - 10:00' },
    { value: '10:00-12:00', label: '10:00 - 12:00' },
    { value: '12:00-14:00', label: '12:00 - 14:00' },
    { value: '14:00-16:00', label: '14:00 - 16:00' },
    { value: '16:00-18:00', label: '16:00 - 18:00' },
    { value: '18:00-20:00', label: '18:00 - 20:00' },
  ];

  const getAvailableTimeSlots = () => {
    if (!pickupDate) return timeSlots;
    const selectedDate = new Date(pickupDate + 'T12:00:00');
    const dayOfWeek = selectedDate.getDay();
    if (dayOfWeek === 0 || dayOfWeek === 6) {
      return [
        { value: '08:00-10:00', label: '08:00 - 10:00' },
        { value: '18:00-20:00', label: '18:00 - 20:00' },
      ];
    }
    if (dayOfWeek === 5) {
      return [
        { value: '08:00-10:00', label: '08:00 - 10:00' },
        { value: '10:00-12:00', label: '10:00 - 12:00' },
      ];
    }
    return timeSlots;
  };

  // Calendar helpers
  const getMinPickupDate = () => {
    const now = new Date();
    const minDate = new Date(now.getTime() + 48 * 60 * 60 * 1000);
    minDate.setHours(0, 0, 0, 0);
    return minDate;
  };
  const isWeekend = (date: Date) => date.getDay() === 0 || date.getDay() === 6;
  const isCzechHoliday = (date: Date) => {
    const m = date.getMonth() + 1,
      d = date.getDate();
    const holidays = [
      [1, 1],
      [5, 1],
      [5, 8],
      [7, 5],
      [7, 6],
      [9, 28],
      [10, 28],
      [11, 17],
      [12, 24],
      [12, 25],
      [12, 26],
    ];
    return holidays.some(([hm, hd]) => hm === m && hd === d);
  };
  const isDateDisabled = (date: Date) => {
    const minDate = getMinPickupDate();
    if (date < minDate) return true;
    if (!isWeekendPickup && isWeekend(date)) return true;
    if (isCzechHoliday(date)) return true;
    return false;
  };
  const generateCalendarDays = () => {
    const year = calendarMonth.getFullYear();
    const month = calendarMonth.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const days: (Date | null)[] = [];

    const jsDay = firstDay.getDay();
    const startDayOfWeek = jsDay === 0 ? 6 : jsDay - 1;
    for (let i = 0; i < startDayOfWeek; i++) days.push(null);
    for (let d = 1; d <= lastDay.getDate(); d++) days.push(new Date(year, month, d, 12, 0, 0));
    return days;
  };
  const formatDate = (date: Date) => {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  };
  const formatDisplayDate = (dateString: string) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('cs-CZ', {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    });
  };
  const handleDateSelect = (date: Date) => {
    if (isDateDisabled(date)) return;
    setPickupDate(formatDate(date));
    setShowCalendar(false);
    setPickupTimeSlot('');
  };

  // Cart manipulation helpers
  const addService = (serviceName: string, basePrice: number) => {
    const existing = cartItems.find((item) => item.service === serviceName);
    if (existing) {
      updateQuantity(cartItems.indexOf(existing), 1);
    } else {
      const newItems = [...cartItems, { service: serviceName, basePrice, quantity: 1 }];
      setCartItems(newItems);
      localStorage.setItem(
        'quickCart',
        JSON.stringify({ items: newItems, location: detectedLocation || 'Praha' })
      );
    }
  };

  const updateQuantity = (index: number, delta: number) => {
    const newItems = [...cartItems];
    newItems[index].quantity = Math.max(1, newItems[index].quantity + delta);
    setCartItems(newItems);
    localStorage.setItem(
      'quickCart',
      JSON.stringify({ items: newItems, location: detectedLocation || 'Praha' })
    );
  };

  const removeItem = (index: number) => {
    const newItems = cartItems.filter((_, i) => i !== index);
    setCartItems(newItems);
    localStorage.setItem(
      'quickCart',
      JSON.stringify({ items: newItems, location: detectedLocation || 'Praha' })
    );
  };

  // ---------- Membership handling ----------
  const verifyMembership = async () => {
    if (!membershipNumber.trim()) {
      setMembershipError('Zadejte prosím členské číslo');
      return;
    }

    setIsVerifyingMembership(true);
    setMembershipError('');

    try {
      const { data, error } = await supabase
        .from('memberships')
        .select('*')
        .eq('membership_code', membershipNumber.trim())
        .eq('status', 'active')
        .maybeSingle();

      if (error || !data) {
        setMembershipError('Členství nenalezeno nebo není aktivní');
        return;
      }

      setMembership(data);
      setMembershipError('');
    } catch (err) {
      setMembershipError('Chyba při ověřování členství');
    } finally {
      setIsVerifyingMembership(false);
    }
  };

  const logoutMembership = () => {
    setMembership(null);
    setMembershipNumber('');
    setMembershipError('');
  };

  // Handle in-cart membership upgrade
  const handleUpgradeMembership = (planName: string) => {
    let discount = 0;
    let limit = 0;
    let price = 0;

    if (planName === 'PREMIUM') {
      discount = 20;
      limit = 20;
      price = 999;
    } else if (planName === 'VIP') {
      discount = 30;
      limit = 30;
      price = 1799;
    }

    // Odečíst cenu aktuálního plánu
    const currentPlanPrices: Record<string, number> = { BASIC: 599, PREMIUM: 999, VIP: 1799 };
    const currentPlanName = membership?.plan_name || (membershipDiscount === 15 ? 'BASIC' : membershipDiscount === 20 ? 'PREMIUM' : 'VIP');
    const currentPrice = currentPlanPrices[currentPlanName] || 0;
    const upgradePrice = price - currentPrice;

    handleUpgradeInCart(planName, discount, limit, upgradePrice);
  };

  const handleUpgradeInCart = (
    planName: string,
    discount: number,
    limit: number,
    upgradePrice: number = 0
  ) => {
    // Save original membership before upgrading
    if (!originalMembership && membership) {
      setOriginalMembership({
        plan_name: membership.plan_name,
        discount: membership.discount || membershipDiscount,
        membership_code: membership.membership_code,
      });
    }

    const upgradeData = { planName, discount, limit, price: upgradePrice };
    setPendingUpgrade(upgradeData);
    setMembership({ ...membership, plan_name: planName, discount });
    setMembershipDiscount(discount);
    setMembershipStatus({
      type: 'success',
      message: `Členství ${planName} aplikováno! Sleva ${discount}% bude aktivní.`,
    });
    // Save to localStorage so it persists if user navigates away and comes back
    localStorage.setItem('cartMembershipUpgrade', JSON.stringify(upgradeData));
  };

  // Order submission
  const handleSubmitOrder = async () => {
    if (!customerName.trim() || !customerEmail.trim() || !customerPhone.trim()) {
      setSubmitError('Vyplňte kontaktní údaje');
      return;
    }
    if (!deliveryStreet.trim() || !deliveryCity.trim() || !deliveryZip.trim()) {
      setSubmitError('Vyplňte adresu');
      return;
    }
    if (cartItems.length === 0) {
      setSubmitError('Košík je prázdný');
      return;
    }

    setIsSubmitting(true);
    setSubmitError('');

    try {
      const orderNumber = `VZ${Date.now().toString().slice(-8)}`;
      const fullAddress = `${deliveryStreet}, ${deliveryCity}, ${deliveryZip}`;

      // Vypočítat správnou cenu dopravy
      const shippingCost = getShippingCost();

      const orderData = {
        orderNumber,
        customerName,
        customerEmail,
        customerPhone,
        deliveryAddress: fullAddress,
        location: detectedLocation,
        pickupDate,
        pickupTimeSlot,
        services: cartItems.map((item) => ({
          service: item.service,
          price: getItemPrice(item.basePrice),
          quantity: item.quantity,
        })),
        subtotal: getSubtotal(),
        shipping: shippingCost,
        hangerTotal: getHangerCost(),
        packagingTotal: getBagCost(),
        weekendPickup: isWeekendPickup && !isVipMember() ? 299 : 0,
        membershipUpgradePrice: getUpgradePrice(),
        totalPrice: getTotalPrice(),
        message,
        isExpressDelivery,
        isWeekendPickup,
        membershipCode: membership?.membership_code || '',
        membershipPlan: membership?.plan_name || (membershipDiscount === 15 ? 'BASIC' : membershipDiscount === 20 ? 'PREMIUM' : 'VIP'),
        pendingMembershipUpgrade: pendingUpgrade ? pendingUpgrade.planName : null,
        pendingMembershipUpgradePrice: pendingUpgrade ? pendingUpgrade.price : 0,
      };

      await fetch(`${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/send-order-email`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY}`,
        },
        body: JSON.stringify({
          orderData,
          customerEmail: customerEmail.trim(),
          customerName: customerName.trim(),
        }),
      });

      localStorage.setItem('lastOrder', JSON.stringify(orderData));
      localStorage.removeItem('quickCart');
      localStorage.removeItem('cartMembershipUpgrade');
      setCartItems([]);
      setSubmitSuccess(true);
      setTimeout(() => navigate(`/objednavka-potvrzena?order=${orderNumber}`), 2000);
    } catch (e: any) {
      setSubmitError(e.message || 'Chyba odeslání');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Membership code verification function
  const checkMembership = async () => {
    if (!membershipCode.trim()) {
      setMembershipStatus({
        type: 'error',
        message: 'Zadejte prosím členské číslo',
      });
      return;
    }

    setCheckingMembership(true);
    setMembershipStatus(null);

    try {
      // Testovací kódy pro demo účely
      const testCodes: Record<string, { plan: 'BASIC' | 'PREMIUM' | 'VIP'; discount: number }> = {
        'BASIC-DEMO-001': { plan: 'BASIC', discount: 15 },
        'PREMIUM-DEMO-001': { plan: 'PREMIUM', discount: 20 },
        'VIP-DEMO-001': { plan: 'VIP', discount: 30 },
      };

      const upperCode = membershipCode.toUpperCase().trim();

      // Kontrola testovacích kódů
      if (testCodes[upperCode]) {
        const { plan, discount } = testCodes[upperCode];
        setMembership({ plan_name: plan, discount, membership_code: upperCode });
        setMembershipDiscount(discount);
        setMembershipStatus({
          type: 'success',
          message: `Členství ${plan} ověřeno! Sleva ${discount}% bude aplikována.`,
        });
        setCheckingMembership(false);
        return;
      }

      // Kontrola v databázi
      const { data, error } = await supabase
        .from('memberships')
        .select('plan_name, status, membership_code')
        .eq('membership_code', upperCode)
        .eq('status', 'active')
        .maybeSingle();

      if (error) throw error;

      if (data && data.status === 'active') {
        // Mapování názvů plánů na slevy
        const discountMap: Record<string, number> = {
          BASIC: 15,
          PREMIUM: 20,
          VIP: 30,
          Standard: 15, // Standard = BASIC (15% sleva)
        };
        
        // Normalizace názvu plánu
        let normalizedPlanName = data.plan_name;
        if (data.plan_name === 'Standard') {
          normalizedPlanName = 'BASIC';
        }
        
        const discount = discountMap[data.plan_name] || 15; // Výchozí 15% pokud není rozpoznán
        
        setMembership({ 
          plan_name: normalizedPlanName, 
          discount, 
          membership_code: data.membership_code 
        });
        setMembershipDiscount(discount);
        setMembershipStatus({
          type: 'success',
          message: `Členství ${normalizedPlanName} ověřeno! Sleva ${discount}% bude aplikována.`,
        });
      } else {
        setMembershipStatus({
          type: 'warning',
          message: 'Členství nebylo nalezeno nebo není aktivní. Můžete pokračovat bez slevy.',
        });
      }
    } catch (error) {
      console.error('Chyba při ověřování členství:', error);
      setMembershipStatus({
        type: 'error',
        message: 'Nepodařilo se ověřit členství. Zkuste to prosím znovu.',
      });
    } finally {
      setCheckingMembership(false);
    }
  };

  const handleVerifyMembership = async () => {
    if (!membershipCode.trim()) {
      setMembershipStatus({
        type: 'warning',
        message: 'Prosím zadejte členské číslo.',
      });
      return;
    }

    setCheckingMembership(true);
    setMembershipStatus(null);

    try {
      // Nejdřív zkusíme testovací kódy
      const testCode = membershipCode.toUpperCase();
      if (testCode === 'BASIC-DEMO-001') {
        setMembership({ plan_name: 'BASIC', discount: 15 });
        setMembershipDiscount(15);
        setMembershipStatus({
          type: 'success',
          message: 'Členství BASIC ověřeno! Sleva 15% bude aplikována.',
        });
        setCheckingMembership(false);
        return;
      } else if (testCode === 'PREMIUM-DEMO-001') {
        setMembership({ plan_name: 'PREMIUM', discount: 20 });
        setMembershipDiscount(20);
        setMembershipStatus({
          type: 'success',
          message: 'Členství PREMIUM ověřeno! Sleva 20% bude aplikována.',
        });
        setCheckingMembership(false);
        return;
      } else if (testCode === 'VIP-DEMO-001') {
        setMembership({ plan_name: 'VIP', discount: 30 });
        setMembershipDiscount(30);
        setMembershipStatus({
          type: 'success',
          message: 'Členství VIP ověřeno! Sleva 30% bude aplikována.',
        });
        setCheckingMembership(false);
        return;
      }

      // Pokud to není testovací kód, zkusíme databázi
      const { data, error } = await supabase
        .from('memberships')
        .select('plan_name, status, membership_code')
        .eq('membership_code', testCode)
        .eq('status', 'active')
        .maybeSingle();

      if (error) throw error;

      if (data) {
        // Mapování názvů plánů na slevy
        const discountMap: Record<string, number> = {
          BASIC: 15,
          PREMIUM: 20,
          VIP: 30,
          Standard: 15, // Standard = BASIC (15% sleva)
        };
        
        // Normalizace názvu plánu
        let normalizedPlanName = data.plan_name;
        if (data.plan_name === 'Standard') {
          normalizedPlanName = 'BASIC';
        }
        
        const discount = discountMap[data.plan_name] || 15;
        
        setMembership({ 
          plan_name: normalizedPlanName, 
          discount, 
          membership_code: data.membership_code 
        });
        setMembershipDiscount(discount);
        setMembershipStatus({
          type: 'success',
          message: `Členství ${normalizedPlanName} ověřeno! Sleva ${discount}% bude aplikována.`,
        });
      } else {
        setMembershipStatus({
          type: 'warning',
          message: 'Členství nebylo nalezeno nebo není aktivní. Můžete pokračovat bez slevy.',
        });
      }
    } catch (error) {
      console.error('Chyba při ověřování členství:', error);
      setMembershipStatus({
        type: 'error',
        message: 'Nepodařilo se ověřit členství. Zkuste to prosím znovu.',
      });
    } finally {
      setCheckingMembership(false);
    }
  };

  // Calculate expected delivery date
  const calculateDeliveryDate = (
    pickupDateStr: string,
    isExpress: boolean,
    membershipPlan?: string
  ): string => {
    if (!pickupDateStr) return '';

    const pickup = new Date(pickupDateStr + 'T12:00:00');
    let deliveryDate = new Date(pickup);

    // VIP členství nebo Express služba - dodání do 24h (funguje i o víkendu)
    if (isExpress || membershipPlan === 'VIP' || membershipDiscount === 30) {
      deliveryDate.setDate(deliveryDate.getDate() + 1);
      return deliveryDate.toLocaleDateString('cs-CZ', {
        weekday: 'long',
        day: 'numeric',
        month: 'long',
        year: 'numeric',
      });
    }

    // PREMIUM členství - dodání do 48 hodin (2 pracovní dny)
    if (membershipPlan === 'PREMIUM' || membershipDiscount === 20) {
      let workingDaysToAdd = 2; // 2 pracovní dny
      let daysAdded = 0;

      while (workingDaysToAdd > 0) {
        daysAdded++;
        deliveryDate.setDate(pickup.getDate() + daysAdded);
        const dayOfWeek = deliveryDate.getDay();
        if (dayOfWeek !== 0 && dayOfWeek !== 6) {
          workingDaysToAdd--;
        }
      }

      return deliveryDate.toLocaleDateString('cs-CZ', {
        weekday: 'long',
        day: 'numeric',
        month: 'long',
        year: 'numeric',
      });
    }

    // BASIC nebo bez členství - standardní dodání 72 hodin (3 pracovní dny)
    let workingDaysToAdd = 3;
    let daysAdded = 0;

    while (workingDaysToAdd > 0) {
      daysAdded++;
      deliveryDate.setDate(pickup.getDate() + daysAdded);
      const dayOfWeek = deliveryDate.getDay();
      if (dayOfWeek !== 0 && dayOfWeek !== 6) {
        workingDaysToAdd--;
      }
    }

    return deliveryDate.toLocaleDateString('cs-CZ', {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    });
  };

  const getExpectedDeliveryDate = () => {
    const membershipPlan =
      membership?.plan_name ||
      (membershipDiscount === 15 ? 'BASIC' : membershipDiscount === 20 ? 'PREMIUM' : membershipDiscount === 30 ? 'VIP' : '');
    return calculateDeliveryDate(pickupDate, isExpressDelivery, membershipPlan);
  };

  const getDeliveryTimeDescription = () => {
    if (isExpressDelivery || isVipMember()) {
      return '⚡ Express služba - dodání do 24 hodin (i o víkendu)';
    }
    if (membership?.plan_name === 'PREMIUM' || membershipDiscount === 20) {
      return '📦 PREMIUM služba - dodání do 48 hodin (2 pracovní dny)';
    }
    return '📦 BASIC služba - dodání do 72 hodin (3 pracovní dny)';
  };

  // Kontrola, zda je objednávka do 5 dnů
  const isShortTermOrder = () => {
    if (!pickupDate) return false;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const pickup = new Date(pickupDate + 'T12:00:00');
    const diffTime = pickup.getTime() - today.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays <= 5;
  };

  // Detekce blížícího se limitu (70%+ využití, ale ještě nepřekročeno)
  const isApproachingLimit = (): boolean => {
    if (!membership && membershipDiscount === 0) return false;
    if (pendingUpgrade) return false; // Už má upgrade
    const limit = getMembershipPiecesLimit();
    if (limit === 0) return false;
    const total = getTotalItems();
    const usage = total / limit;
    return usage >= 0.7 && total <= limit;
  };

  // State pro animaci změny doby doručení
  const [deliveryDateChanged, setDeliveryDateChanged] = useState(false);
  const prevDeliveryDateRef = useRef<string>('');

  // Automatické přepočítání doby doručení při změně členství
  useEffect(() => {
    if (!pickupDate) return;
    
    const newDeliveryDate = getExpectedDeliveryDate();
    
    // Pokud se změnila doba doručení, zobrazit animaci
    if (prevDeliveryDateRef.current && prevDeliveryDateRef.current !== newDeliveryDate) {
      setDeliveryDateChanged(true);
      setTimeout(() => setDeliveryDateChanged(false), 2000);
    }
    
    prevDeliveryDateRef.current = newDeliveryDate;
  }, [membership, membershipDiscount, isExpressDelivery, pickupDate]);

  return (
    <>
      <SEOHead
        title={t('seo.cart.title')}
        description={t('seo.cart.description')}
        keywords={t('seo.cart.keywords')}
      />
      <div className="min-h-screen bg-gradient-to-br from-teal-50 via-white to-teal-50">
        <SEOHead />
        <Navbar />
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-32">
          {/* Header */}
          <div className="mb-4 sm:mb-6">
            <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 mb-2 flex items-center gap-2 sm:gap-3">
              <i className="ri-shopping-cart-2-fill text-teal-600 text-2xl sm:text-3xl"></i>
              Nákupní košík
            </h1>
            <p className="text-sm sm:text-base text-gray-600">{getTotalItems()} položek</p>
          </div>

          {(membership || membershipDiscount > 0) && (
            <div className="mb-4 sm:mb-6 bg-gradient-to-r from-teal-500 to-emerald-500 rounded-xl sm:rounded-2xl p-3 sm:p-4 border border-amber-200 shadow-lg text-white">
              <div className="flex items-start justify-between">
                <div className="flex items-start gap-2 sm:gap-3">
                  <div className="w-8 h-8 sm:w-10 sm:h-10 bg-white/20 rounded-full flex items-center justify-center">
                    <i className="ri-vip-crown-fill text-lg sm:text-xl"></i>
                  </div>
                  <div>
                    <h3 className="text-base sm:text-lg font-semibold mb-1">
                      {membership?.plan_name || (membershipDiscount === 15 ? 'BASIC' : membershipDiscount === 20 ? 'PREMIUM' : 'VIP')} Členství
                    </h3>
                    <p className="text-xs sm:text-sm text-white/80">
                      Sleva {getDiscountPercent()}% na {getMembershipPiecesLimit()} kusů
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => {
                    setMembership(null);
                    setMembershipDiscount(0);
                    setMembershipCode('');
                    setMembershipStatus(null);
                  }}
                  className="text-xs sm:text-sm underline hover:text-gray-200 whitespace-nowrap"
                >
                  Odhlásit
                </button>
              </div>

              {/* Ukazatel využití kusů */}
              <div className="mt-3 bg-white/10 rounded-lg p-2.5">
                <div className="flex items-center justify-between text-xs sm:text-sm mb-1.5">
                  <span className="text-white/90">Využití členství</span>
                  <span className="font-semibold">{getDiscountedPiecesUsed()} / {getMembershipPiecesLimit()} ks</span>
                </div>
                <div className="w-full bg-white/20 rounded-full h-2">
                  <div 
                    className={`h-2 rounded-full transition-all ${
                      getOverLimitPieces() > 0 ? 'bg-amber-400' : 'bg-white'
                    }`}
                    style={{ width: `${Math.min(100, (getTotalItems() / getMembershipPiecesLimit()) * 100)}%` }}
                  ></div>
                </div>
              </div>
            </div>
          )}

          {/* Upozornění na překročení limitu s možností upgradu */}
          {(membership || membershipDiscount > 0) && getOverLimitPieces() > 0 && !pendingUpgrade && (
            <div className="mb-4 sm:mb-6 bg-amber-50 border-2 border-amber-300 rounded-xl p-3 sm:p-4">
              <div className="flex items-start gap-2 sm:gap-3">
                <div className="w-8 h-8 sm:w-10 sm:h-10 bg-amber-500 rounded-lg flex items-center justify-center flex-shrink-0">
                  <i className="ri-error-warning-line text-lg sm:text-xl text-white"></i>
                </div>
                <div className="flex-1">
                  <h4 className="font-bold text-amber-900 text-sm mb-1">
                    Překročen limit členství
                  </h4>
                  <p className="text-xs sm:text-sm text-amber-800">
                    Váš plán zahrnuje <strong>{getMembershipPiecesLimit()} kusů</strong> se slevou {getDiscountPercent()}%. 
                    Máte v košíku <strong>{getTotalItems()} kusů</strong> — zbývajících <strong>{getOverLimitPieces()} ks</strong> bude 
                    účtováno za <strong>plnou cenu bez slevy</strong>.
                  </p>
                  <div className="mt-2 flex flex-wrap gap-3 text-xs sm:text-sm">
                    <span className="inline-flex items-center gap-1 text-teal-700 bg-teal-50 px-2 py-0.5 rounded-md">
                      <i className="ri-check-line"></i>
                      {getDiscountedPiecesUsed()} ks se slevou {getDiscountPercent()}%
                    </span>
                    <span className="inline-flex items-center gap-1 text-amber-700 bg-amber-100 px-2 py-0.5 rounded-md">
                      <i className="ri-price-tag-3-line"></i>
                      {getOverLimitPieces()} ks za plnou cenu
                    </span>
                  </div>

                  {/* Nabídka upgradu přímo v upozornění */}
                  <div className="mt-4 pt-3 border-t border-amber-200">
                    <p className="text-xs sm:text-sm font-semibold text-gray-900 mb-2 flex items-center gap-1">
                      <i className="ri-arrow-up-circle-line text-teal-600"></i>
                      Zvažte upgrade pro větší slevy:
                    </p>
                    <div className="space-y-2">
                      {/* PREMIUM upgrade - zobrazit pouze pokud je aktuální plán BASIC */}
                      {(membership?.plan_name === 'BASIC' || membershipDiscount === 15) && (
                        <div className="flex items-center justify-between bg-white p-3 rounded-lg border border-gray-200">
                          <div>
                            <p className="font-semibold text-gray-900 text-sm">PREMIUM</p>
                            <p className="text-xs text-gray-600">20% sleva na 20 kusů</p>
                          </div>
                          <button
                            onClick={() => handleUpgradeInCart('PREMIUM', 20, 20, 400)}
                            className="px-4 py-2 bg-gradient-to-r from-teal-600 to-emerald-500 text-white rounded-lg hover:from-teal-700 hover:to-emerald-600 transition-all text-sm font-medium whitespace-nowrap cursor-pointer flex items-center gap-1"
                          >
                            <i className="ri-shopping-cart-fill"></i>
                            +400 Kč
                          </button>
                        </div>
                      )}
                      {/* VIP upgrade - zobrazit pro BASIC i PREMIUM */}
                      {(membership?.plan_name === 'BASIC' || membership?.plan_name === 'PREMIUM' || membershipDiscount === 15 || membershipDiscount === 20) && (
                        <div className="flex items-center justify-between bg-white p-3 rounded-lg border border-gray-200">
                          <div>
                            <p className="font-semibold text-gray-900 text-sm">VIP</p>
                            <p className="text-xs text-gray-600">30% sleva na 30 kusů + doprava zdarma</p>
                          </div>
                          <button
                            onClick={() => {
                              const currentPrice = membership?.plan_name === 'PREMIUM' || membershipDiscount === 20 ? 999 : 599;
                              const upgradePrice = 1799 - currentPrice;
                              handleUpgradeInCart('VIP', 30, 30, upgradePrice);
                            }}
                            className="px-4 py-2 bg-gradient-to-r from-teal-600 to-emerald-500 text-white rounded-lg hover:from-teal-700 hover:to-emerald-600 transition-all text-sm font-medium whitespace-nowrap cursor-pointer flex items-center gap-1"
                          >
                            <i className="ri-shopping-cart-fill"></i>
                            +{membership?.plan_name === 'PREMIUM' || membershipDiscount === 20 ? '800' : '1200'} Kč
                          </button>
                        </div>
                      )}
                    </div>
                    <p className="text-[10px] text-gray-500 mt-2">
                      <i className="ri-information-line mr-0.5"></i>
                      Cena upgradu bude přidána k objednávce a zaplatíte vše najednou
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Nabídka upgradu členství - rozšířená verze (zobrazí se po kliknutí na upgrade) */}
          {(membership || membershipDiscount > 0) && getOverLimitPieces() > 0 && pendingUpgrade && (
            <MembershipUpgrade
              currentPlan={membership?.plan_name || (membershipDiscount === 15 ? 'BASIC' : membershipDiscount === 20 ? 'PREMIUM' : 'VIP')}
              currentDiscount={getDiscountPercent()}
              currentLimit={getMembershipPiecesLimit()}
              totalItems={getTotalItems()}
              overLimitPieces={getOverLimitPieces()}
              cartItems={cartItems}
              onUpgradeInCart={handleUpgradeInCart}
            />
          )}

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 sm:gap-8">
            {/* Left Column - Cart Items & Form */}
            <div className="lg:col-span-2 space-y-4 sm:space-y-6">
              {/* Membership Verification */}
              {!membership && (
                <div className="bg-gradient-to-br from-amber-50 to-orange-50 rounded-xl p-4 sm:p-6 border border-amber-200">
                  <div className="flex items-start gap-2 sm:gap-3 mb-3 sm:mb-4">
                    <div className="w-8 h-8 sm:w-10 sm:h-10 bg-gradient-to-br from-amber-500 to-orange-500 rounded-lg flex items-center justify-center flex-shrink-0">
                      <i className="ri-vip-crown-line text-lg sm:text-xl text-white"></i>
                    </div>
                    <div>
                      <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-1">
                        Máte členství?
                      </h3>
                      <p className="text-xs sm:text-sm text-gray-600">
                        Zadejte své členské číslo a získejte slevu 15-30%
                      </p>
                    </div>
                  </div>

                  <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
                    <input
                      type="text"
                      value={membershipCode}
                      onChange={(e) => setMembershipCode(e.target.value.toUpperCase())}
                      placeholder="BASIC-DEMO-002"
                      className="flex-1 px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                    />
                    <button
                      onClick={checkMembership}
                      disabled={checkingMembership || !membershipCode.trim()}
                      className="px-5 sm:px-6 py-2.5 sm:py-3 bg-gradient-to-r from-amber-500 to-orange-500 text-white rounded-lg hover:from-amber-600 hover:to-orange-600 transition-all disabled:opacity-50 disabled:cursor-not-allowed font-medium whitespace-nowrap text-sm sm:text-base cursor-pointer"
                    >
                      {checkingMembership ? (
                        <i className="ri-loader-4-line animate-spin"></i>
                      ) : (
                        'Ověřit'
                      )}
                    </button>
                  </div>

                  {membershipStatus && (
                    <div className={`mt-3 sm:mt-4 p-3 sm:p-4 rounded-lg ${
                      membershipStatus.type === 'success' 
                        ? 'bg-green-50 border border-green-200' 
                        : 'bg-yellow-50 border border-yellow-200'
                    }`}>
                      <p className={`text-xs sm:text-sm ${
                        membershipStatus.type === 'success' 
                          ? 'text-green-800' 
                          : 'text-yellow-800'
                      }`}>
                        {membershipStatus.message}
                      </p>
                    </div>
                  )}
                </div>
              )}

              {/* Cart Items */}
              <div className="bg-white rounded-lg shadow-sm p-4 sm:p-6">
                <CartServices
                  cartItems={cartItems}
                  membership={membership}
                  membershipDiscount={membershipDiscount}
                  getItemPrice={getItemPrice}
                  getDiscountPercent={getDiscountPercent}
                  addService={addService}
                  updateQuantity={updateQuantity}
                  removeItem={removeItem}
                  getTotalItems={getTotalItems}
                  membershipPiecesLimit={getMembershipPiecesLimit()}
                  overLimitPieces={getOverLimitPieces()}
                />
                <HangersAndBags
                  hangerCount={hangerCount}
                  setHangerCount={setHangerCount}
                  bagCount={bagCount}
                  setBagCount={setBagCount}
                  useOwnHanger={useOwnHanger}
                  setUseOwnHanger={setUseOwnHanger}
                  useOwnBag={useOwnBag}
                  setUseOwnBag={setUseOwnBag}
                  getHangerCost={getHangerCost}
                  getBagCost={getBagCost}
                />
              </div>
            </div>

            {/* Right Column - Order Summary */}
            <div className="lg:col-span-1">
              <div className="bg-white rounded-xl sm:rounded-2xl shadow-lg p-4 sm:p-6 lg:sticky lg:top-28">
                <h2 className="text-lg sm:text-xl font-bold mb-4 sm:mb-6 flex items-center gap-2">
                  <i className="ri-file-list-3-line text-pink-600"></i>
                  Dokončení objednávky
                </h2>

                {/* Contact info */}
                <div className="mb-4 sm:mb-6">
                  <h3 className="font-semibold text-sm sm:text-base mb-2 sm:mb-3 flex items-center gap-2">
                    <i className="ri-user-line text-teal-600"></i>
                    Kontaktní údaje *
                  </h3>
                  <div className="space-y-2 sm:space-y-3">
                    <input
                      placeholder="Jméno a příjmení *"
                      value={customerName}
                      onChange={(e) => setCustomerName(e.target.value)}
                      className="w-full px-3 sm:px-4 py-2.5 sm:py-3 border-2 border-gray-200 rounded-xl focus:border-teal-500 text-sm sm:text-base"
                    />
                    <input
                      placeholder="Email *"
                      type="email"
                      value={customerEmail}
                      onChange={(e) => setCustomerEmail(e.target.value)}
                      className="w-full px-3 sm:px-4 py-2.5 sm:py-3 border-2 border-gray-200 rounded-xl focus:border-teal-500 text-sm sm:text-base"
                    />
                    <input
                      placeholder="Telefon *"
                      type="tel"
                      value={customerPhone}
                      onChange={(e) => setCustomerPhone(e.target.value)}
                      className="w-full px-3 sm:px-4 py-2.5 sm:py-3 border-2 border-gray-200 rounded-xl focus:border-teal-500 text-sm sm:text-base"
                    />
                  </div>
                </div>

                {/* Address */}
                <div className="mb-4 sm:mb-6">
                  <h3 className="font-semibold text-sm sm:text-base mb-2 sm:mb-3 flex items-center gap-2">
                    <i className="ri-map-pin-line text-teal-600"></i>
                    Adresa vyzvednutí/dodání *
                  </h3>
                  <div className="space-y-2 sm:space-y-3">
                    <div className="relative">
                      <div className="relative">
                        <input
                          placeholder="Ulice a číslo popisné *"
                          value={deliveryStreet}
                          onChange={(e) => setDeliveryStreet(e.target.value)}
                          onFocus={() => {
                            if (onlineAddressSuggestions.length > 0 || localStreetSuggestions.length > 0 || streetSuggestions.length > 0)
                              setShowSuggestions(true);
                          }}
                          autoComplete="off"
                          className="w-full px-3 sm:px-4 py-2.5 sm:py-3 border-2 border-gray-200 rounded-xl focus:border-teal-500 text-sm sm:text-base pr-10"
                        />
                        {isSearchingAddress && (
                          <div className="absolute right-3 top-1/2 -translate-y-1/2">
                            <i className="ri-loader-4-line animate-spin text-teal-500"></i>
                          </div>
                        )}
                      </div>
                      {showSuggestions && (localStreetSuggestions.length > 0 || onlineAddressSuggestions.length > 0 || streetSuggestions.length > 0) && (
                        <div
                          ref={suggestionsRef}
                          className="absolute top-full left-0 right-0 mt-2 bg-white border-2 border-gray-200 rounded-xl shadow-xl z-50 max-h-80 overflow-y-auto"
                        >
                          {localStreetSuggestions.length > 0 && (
                            <>
                              {localStreetSuggestions.map((street, index) => {
                                const zipInfo = getZipForStreet(street);
                                return (
                                  <button
                                    key={`local-${index}`}
                                    type="button"
                                    onClick={() => handleSelectLocalSuggestion(street)}
                                    className="w-full px-3 sm:px-4 py-2 text-left hover:bg-teal-50 flex items-center gap-2 transition-colors cursor-pointer border-b border-gray-50 last:border-b-0"
                                  >
                                    <div className="w-6 h-6 flex items-center justify-center flex-shrink-0">
                                      <i className="ri-road-map-line text-teal-500"></i>
                                    </div>
                                    <div className="flex-1 min-w-0">
                                      <span className="font-medium text-gray-900 text-sm block truncate">{street}</span>
                                      {zipInfo && (
                                        <span className="text-xs text-gray-500">{zipInfo.city}, {zipInfo.zip}</span>
                                      )}
                                    </div>
                                  </button>
                                );
                              })}
                            </>
                          )}
                          {onlineAddressSuggestions.length > 0 && (
                            <>
                              {(localStreetSuggestions.length > 0) && (
                                <div className="px-3 py-1.5 bg-gray-50 border-y border-gray-100">
                                  <span className="text-xs text-gray-500 font-medium flex items-center gap-1">
                                    <i className="ri-global-line"></i>
                                    Výsledky z celé ČR
                                  </span>
                                </div>
                              )}
                              {onlineAddressSuggestions.map((suggestion, index) => (
                                <button
                                  key={`online-${index}`}
                                  type="button"
                                  onClick={() => handleSelectOnlineSuggestion(suggestion)}
                                  className="w-full px-3 sm:px-4 py-2 text-left hover:bg-teal-50 flex items-center gap-2 transition-colors cursor-pointer border-b border-gray-50 last:border-b-0"
                                >
                                  <div className="w-6 h-6 flex items-center justify-center flex-shrink-0">
                                    <i className={`${suggestion.source === 'osm' ? 'ri-map-pin-line' : 'ri-building-line'} text-emerald-500`}></i>
                                  </div>
                                  <div className="flex-1 min-w-0">
                                    <span className="font-medium text-gray-900 text-sm block truncate">{suggestion.street}</span>
                                    <span className="text-xs text-gray-500">
                                      {suggestion.city}{suggestion.zip ? `, ${suggestion.zip}` : ''}
                                    </span>
                                  </div>
                                </button>
                              ))}
                            </>
                          )}
                          {streetSuggestions.length > 0 && (
                            <>
                              {(localStreetSuggestions.length > 0 || onlineAddressSuggestions.length > 0) && (
                                <div className="px-3 py-1.5 bg-gray-50 border-y border-gray-100">
                                  <span className="text-xs text-gray-500 font-medium flex items-center gap-1">
                                    <i className="ri-google-line"></i>
                                    Google Maps
                                  </span>
                                </div>
                              )}
                              {streetSuggestions.map((suggestion) => (
                                <button
                                  key={suggestion.place_id}
                                  type="button"
                                  onClick={() => handleSelectSuggestion(suggestion)}
                                  className="w-full px-3 sm:px-4 py-2 text-left hover:bg-teal-50 flex items-center gap-2 transition-colors cursor-pointer border-b border-gray-50 last:border-b-0"
                                >
                                  <div className="w-6 h-6 flex items-center justify-center flex-shrink-0">
                                    <i className="ri-map-pin-line text-amber-500"></i>
                                  </div>
                                  <div className="flex-1 min-w-0">
                                    <span className="font-medium text-gray-900 text-sm block truncate">{suggestion.description}</span>
                                  </div>
                                </button>
                              ))}
                            </>
                          )}
                          {isSearchingAddress && localStreetSuggestions.length === 0 && onlineAddressSuggestions.length === 0 && (
                            <div className="px-4 py-3 text-center">
                              <i className="ri-loader-4-line animate-spin text-teal-500 mr-2"></i>
                              <span className="text-sm text-gray-500">Hledám adresy v celé ČR...</span>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                    <div className="grid grid-cols-2 gap-2 sm:gap-3">
                      <input
                        placeholder="Město *"
                        value={deliveryCity}
                        onChange={(e) => setDeliveryCity(e.target.value)}
                        className="w-full px-3 sm:px-4 py-2.5 sm:py-3 border-2 border-gray-200 rounded-xl focus:border-teal-500 text-sm sm:text-base"
                      />
                      <input
                        placeholder="PSČ *"
                        value={deliveryZip}
                        onChange={(e) => setDeliveryZip(e.target.value)}
                        maxLength={6}
                        className="w-full px-3 sm:px-4 py-2.5 sm:py-3 border-2 border-gray-200 rounded-xl focus:border-teal-500 text-sm sm:text-base"
                      />
                    </div>
                  </div>
                </div>

                {/* Message */}
                <div className="mb-4 sm:mb-6">
                  <h3 className="font-semibold text-sm sm:text-base mb-2 sm:mb-3 flex items-center gap-2">
                    <i className="ri-message-3-line text-teal-600"></i>
                    Poznámka (volitelné)
                  </h3>
                  <textarea
                    placeholder="Speciální požadavky..."
                    rows={3}
                    maxLength={500}
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    className="w-full px-3 sm:px-4 py-2.5 sm:py-3 border-2 border-gray-200 rounded-xl focus:border-teal-500 text-sm sm:text-base resize-none"
                  />
                </div>

                {/* Pickup date & time */}
                <div className="mb-4 sm:mb-6">
                  <h3 className="font-semibold mb-2 sm:mb-3 flex items-center gap-2">
                    <i className="ri-calendar-schedule-line text-teal-600"></i>
                    Rezervace termínu vyzvednutí
                  </h3>
                  <div className="space-y-2 sm:space-y-3">
                    <div className="relative" ref={calendarRef}>
                      <label className="block text-sm text-gray-600 mb-1">Datum vyzvednutí</label>
                      <button
                        type="button"
                        onClick={() => setShowCalendar(!showCalendar)}
                        className="w-full px-3 sm:px-4 py-2.5 sm:py-3 border-2 border-gray-200 rounded-xl cursor-pointer bg-white text-left flex items-center justify-between hover:border-teal-300 transition-colors"
                      >
                        <span className={pickupDate ? 'text-gray-900' : 'text-gray-400'}>
                          {pickupDate ? formatDisplayDate(pickupDate) : 'dd.mm.rrrr'}
                        </span>
                        <i className="ri-calendar-line text-gray-400"></i>
                      </button>
                      {showCalendar && (
                        <div className="absolute top-full left-0 right-0 mt-2 bg-white border-2 border-gray-200 rounded-xl shadow-xl z-50 p-3 sm:p-4">
                          <div className="flex items-center justify-between mb-2 sm:mb-3">
                            <button
                              type="button"
                              onClick={() =>
                                setCalendarMonth(
                                  new Date(
                                    calendarMonth.getFullYear(),
                                    calendarMonth.getMonth() - 1,
                                    1
                                  )
                                )
                              }
                              className="w-6 h-6 flex items-center justify-center rounded-lg hover:bg-gray-100 cursor-pointer"
                            >
                              <i className="ri-arrow-left-s-line text-xl"></i>
                            </button>
                            <span className="font-semibold text-gray-900">
                              {calendarMonth.toLocaleDateString('cs-CZ', {
                                month: 'long',
                                year: 'numeric',
                              })}
                            </span>
                            <button
                              type="button"
                              onClick={() =>
                                setCalendarMonth(
                                  new Date(
                                    calendarMonth.getFullYear(),
                                    calendarMonth.getMonth() + 1,
                                    1
                                  )
                                )
                              }
                              className="w-6 h-6 flex items-center justify-center rounded-lg hover:bg-gray-100 cursor-pointer"
                            >
                              <i className="ri-arrow-right-s-line text-xl"></i>
                            </button>
                          </div>
                          <div className="grid grid-cols-7 gap-1 mb-2">
                            {['Po', 'Út', 'St', 'Čt', 'Pá', 'So', 'Ne'].map((day, idx) => (
                              <div
                                key={day}
                                className={`text-center text-xs font-medium py-1 ${
                                  idx >= 5 ? 'text-pink-500' : 'text-gray-500'
                                }`}
                              >
                                {day}
                              </div>
                            ))}
                          </div>
                          <div className="grid grid-cols-7 gap-1">
                            {generateCalendarDays().map((date, idx) => {
                              if (!date) return <div key={`empty-${idx}`} className="w-8 h-8"></div>;
                              const disabled = isDateDisabled(date);
                              const isSelected = pickupDate === formatDate(date);
                              const isWeekendDay = isWeekend(date);
                              return (
                                <button
                                  key={formatDate(date)}
                                  type="button"
                                  onClick={() => handleDateSelect(date)}
                                  disabled={disabled}
                                  className={`w-8 h-8 flex items-center justify-center rounded-lg text-sm font-medium transition-all cursor-pointer ${
                                    isSelected
                                      ? 'bg-teal-600 text-white'
                                      : disabled
                                      ? 'text-gray-300 cursor-not-allowed bg-gray-50'
                                      : isWeekendDay
                                      ? 'text-teal-600 hover:bg-teal-100'
                                      : 'text-gray-700 hover:bg-teal-100'
                                  }`}
                                >
                                  {date.getDate()}
                                </button>
                              );
                            })}
                          </div>
                        </div>
                      )}
                    </div>
                    <div>
                      <label className="block text-sm text-gray-600 mb-1">Časové okno</label>
                      <div className="grid grid-cols-2 gap-2">
                        {getAvailableTimeSlots().map((slot) => (
                          <button
                            key={slot.value}
                            type="button"
                            onClick={() => setPickupTimeSlot(slot.value)}
                            disabled={!pickupDate}
                            className={`px-3 py-2 text-sm rounded-lg border-2 transition-all cursor-pointer ${
                              pickupTimeSlot === slot.value
                                ? 'bg-teal-600 text-white border-teal-600'
                                : pickupDate
                                ? 'border-gray-200 hover:border-teal-300 text-gray-700'
                                : 'border-gray-100 text-gray-300 cursor-not-allowed'
                            }`}
                          >
                            {slot.label}
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Upozornění pro objednávky do 5 dnů */}
                    {pickupDate && isShortTermOrder() && (
                      <div className="mt-2 sm:mt-3 p-3 bg-red-50 border-2 border-red-200 rounded-xl">
                        <div className="flex items-start gap-2 sm:gap-3">
                          <div className="w-6 h-6 bg-red-500 rounded-lg flex items-center justify-center flex-shrink-0">
                            <i className="ri-alarm-warning-line text-white"></i>
                          </div>
                          <div className="flex-1">
                            <h4 className="font-bold text-red-800 text-sm mb-1">
                              ⚠️ Objednávka s krátkým termínem
                            </h4>
                            <p className="text-xs text-red-700 mb-2">
                              Svoz je naplánován do 5 dnů. Pro tuto objednávku platí speciální platební podmínky:
                            </p>
                            <ul className="text-xs text-red-700 space-y-1">
                              <li className="flex items-start gap-1">
                                <i className="ri-arrow-right-s-fill flex-shrink-0 mt-0.5"></i>
                                <span><strong>Hotovost při svozu</strong> - platba řidiči při vyzvednutí</span>
                              </li>
                              <li className="flex items-start gap-1">
                                <i className="ri-arrow-right-s-fill flex-shrink-0 mt-0.5"></i>
                                <span><strong>Okamžitý převod</strong> - s potvrzením o platbě před svozem</span>
                              </li>
                            </ul>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Expected Delivery Date Info */}
                    {pickupDate && (
                      <div className={`mt-2 sm:mt-3 p-3 bg-gradient-to-r from-blue-50 to-cyan-50 border-2 rounded-xl transition-all duration-500 ${
                        deliveryDateChanged 
                          ? 'border-green-400 ring-2 ring-green-200 scale-[1.02]' 
                          : 'border-blue-200'
                      }`}>
                        <div className="flex items-start gap-2 sm:gap-3">
                          <div className={`w-6 h-6 rounded-lg flex items-center justify-center flex-shrink-0 transition-colors duration-500 ${
                            deliveryDateChanged ? 'bg-green-500' : 'bg-blue-500'
                          }`}>
                            <i className={`text-white ${deliveryDateChanged ? 'ri-refresh-line animate-spin' : 'ri-truck-line'}`}></i>
                          </div>
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              <h4 className="font-semibold text-gray-900 text-sm mb-1">
                                Předpokládaný dovoz
                              </h4>
                              {deliveryDateChanged && (
                                <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full font-medium animate-pulse">
                                  Aktualizováno
                                </span>
                              )}
                            </div>
                            <p className={`font-medium text-sm transition-colors duration-500 ${
                              deliveryDateChanged ? 'text-green-700' : 'text-blue-700'
                            }`}>
                              {getExpectedDeliveryDate()}
                            </p>
                            <p className="text-xs text-gray-600 mt-1">
                              {getDeliveryTimeDescription()}
                            </p>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Způsob platby */}
                <div className="mb-4 sm:mb-6">
                  <h3 className="font-semibold mb-2 sm:mb-3 flex items-center gap-2">
                    <i className="ri-bank-card-line text-teal-600"></i>
                    Způsob platby
                  </h3>
                  <div className="space-y-2 sm:space-y-3">
                    <label 
                      className={`block p-3 rounded-xl border-2 cursor-pointer transition-all ${
                        paymentMethod === 'bank_transfer' 
                          ? 'border-teal-500 bg-teal-50' 
                          : 'border-gray-200 hover:border-teal-300'
                      } ${isShortTermOrder() ? 'opacity-60' : ''}`}
                    >
                      <div className="flex items-center gap-3">
                        <input
                          type="radio"
                          name="paymentMethod"
                          value="bank_transfer"
                          checked={paymentMethod === 'bank_transfer'}
                          onChange={() => setPaymentMethod('bank_transfer')}
                          disabled={isShortTermOrder()}
                          className="w-4 h-4 text-teal-600 border-gray-300 rounded focus:ring-teal-500"
                        />
                        <div className="w-8 h-8 bg-teal-100 rounded-full flex items-center justify-center">
                          <i className="ri-bank-line text-lg text-teal-600"></i>
                        </div>
                        <div className="flex-1">
                          <p className="font-medium text-gray-900 text-sm">Bankovní převod</p>
                          <p className="text-xs text-gray-500">Splatnost 5 kalendářních dnů</p>
                        </div>
                        {paymentMethod === 'bank_transfer' && !isShortTermOrder() && (
                          <i className="ri-check-line text-xl text-teal-600"></i>
                        )}
                      </div>
                      {isShortTermOrder() && (
                        <p className="text-xs text-red-600 mt-2 ml-7">
                          ⚠️ Nedostupné pro objednávky do 5 dnů
                        </p>
                      )}
                    </label>

                    <label 
                      className={`block p-3 rounded-xl border-2 cursor-pointer transition-all ${
                        paymentMethod === 'cash' 
                          ? 'border-teal-500 bg-teal-50' 
                          : 'border-gray-200 hover:border-teal-300'
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <input
                          type="radio"
                          name="paymentMethod"
                          value="cash"
                          checked={paymentMethod === 'cash'}
                          onChange={() => setPaymentMethod('cash')}
                          className="w-4 h-4 text-teal-600 border-gray-300 rounded focus:ring-teal-500"
                        />
                        <div className="w-8 h-8 bg-amber-100 rounded-full flex items-center justify-center">
                          <i className="ri-money-cny-circle-line text-lg text-amber-600"></i>
                        </div>
                        <div className="flex-1">
                          <p className="font-medium text-gray-900 text-sm">Hotově při svozu</p>
                          <p className="text-xs text-gray-500">Platba řidiči při vyzvednutí prádla</p>
                        </div>
                        {paymentMethod === 'cash' && (
                          <i className="ri-check-line text-xl text-teal-600"></i>
                        )}
                      </div>
                      {isShortTermOrder() && (
                        <p className="text-xs text-green-600 mt-2 ml-7">
                          ✓ Doporučeno pro objednávky do 5 dnů
                        </p>
                      )}
                    </label>
                  </div>
                </div>

                {/* Options */}
                <div className="mb-4 sm:mb-6 space-y-2 sm:space-y-3">
                  <div
                    className={`p-2 sm:p-3 rounded-xl ${
                      isVipMember() ? 'bg-teal-50 border border-teal-200' : 'bg-gray-50'
                    }`}
                  >
                    <label className="flex items-center gap-3 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={isExpressDelivery}
                        onChange={(e) => setIsExpressDelivery(e.target.checked)}
                        className="w-5 h-5 text-teal-600 border-gray-300 rounded focus:ring-teal-500"
                      />
                      <i className="ri-flashlight-fill text-amber-500 text-xl"></i>
                      <span className="font-medium flex-1">
                        Express dodání do 24h
                      </span>
                      {isVipMember() ? (
                        <span className="text-emerald-600 font-bold">ZDARMA</span>
                      ) : (
                        <span className="text-pink-600 font-semibold">+399 Kč</span>
                      )}
                    </label>
                    {isVipMember() && (
                      <p className="text-xs text-teal-600 mt-1 sm:mt-2 ml-8">✨ Zahrnuto ve VIP členství</p>
                    )}
                  </div>
                  <div
                    className={`p-2 sm:p-3 rounded-xl ${
                      isVipMember()
                        ? 'bg-teal-50 border border-teal-200'
                        : 'bg-gray-50'
                    }`}
                  >
                    <label className="flex items-center gap-3 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={isWeekendPickup}
                        onChange={(e) => setIsWeekendPickup(e.target.checked)}
                        className="w-5 h-5 text-teal-600 border-gray-300 rounded focus:ring-teal-500"
                      />
                      <i className="ri-calendar-check-fill text-teal-600 text-xl"></i>
                      <span className="font-medium flex-1">
                        Víkendový svoz
                      </span>
                      {isVipMember() ? (
                        <span className="text-emerald-600 font-bold">ZDARMA</span>
                      ) : (
                        <span className="text-pink-600 font-semibold">+299 Kč</span>
                      )}
                    </label>
                    {isVipMember() && (
                      <p className="text-xs text-teal-600 mt-1 sm:mt-2 ml-8">✨ Zahrnuto ve VIP členství</p>
                    )}
                  </div>
                </div>

                {/* Summary */}
                <div className="border-t-2 border-gray-100 pt-4 sm:pt-6 mb-4 sm:mb-6">
                  <div className="space-y-1.5 sm:space-y-2 text-xs sm:text-sm">
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600">Služby ({getTotalItems()} ks):</span>
                      <div className="flex items-center gap-2">
                        {(membership || membershipDiscount > 0) && getDiscountAmount() > 0 && (
                          <span className="text-gray-400 line-through">{getOriginalSubtotal()} Kč</span>
                        )}
                        <span className="font-semibold">{getSubtotal()} Kč</span>
                      </div>
                    </div>
                    {(membership || membershipDiscount > 0) && getDiscountAmount() > 0 && (
                      <div className="flex justify-between items-center text-teal-600">
                        <span>Členská sleva (−{getDiscountPercent()}% na {getDiscountedPiecesUsed()} ks):</span>
                        <span className="font-semibold">−{getDiscountAmount()} Kč</span>
                      </div>
                    )}
                    {getHangerCost() > 0 && (
                      <div className="flex justify-between items-center">
                        <span className="text-gray-600">Ramínka ({hangerCount} ks):</span>
                        <span className="font-semibold">+{getHangerCost()} Kč</span>
                      </div>
                    )}
                    {getBagCost() > 0 && (
                      <div className="flex justify-between items-center">
                        <span className="text-gray-600">Obaly ({bagCount} ks):</span>
                        <span className="font-semibold">+{getBagCost()} Kč</span>
                      </div>
                    )}
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600">Doprava:</span>
                      {isVipMember() ? (
                        <span className="font-semibold text-emerald-600">ZDARMA</span>
                      ) : (
                        <span className="font-semibold">+{getShippingCost()} Kč</span>
                      )}
                    </div>
                    {isExpressDelivery && (
                      <div className="flex justify-between items-center">
                        <span className="text-gray-600">Express dodání (24h):</span>
                        {isVipMember() ? (
                          <span className="font-semibold text-emerald-600">ZDARMA</span>
                        ) : (
                          <span className="font-semibold">+{getExpressCost()} Kč</span>
                        )}
                      </div>
                    )}
                    {isWeekendPickup && (
                      <div className="flex justify-between items-center">
                        <span className="text-gray-600">Víkendový svoz:</span>
                        {isVipMember() ? (
                          <span className="font-semibold text-emerald-600">ZDARMA</span>
                        ) : (
                          <span className="font-semibold">+{getWeekendCost()} Kč</span>
                        )}
                      </div>
                    )}
                    
                    {/* Upgrade členství v košíku */}
                    {pendingUpgrade && pendingUpgrade.price > 0 && (
                      <div className="flex justify-between items-center bg-teal-50 -mx-2 px-2 py-2 rounded-lg border border-teal-200">
                        <div className="flex items-center gap-2">
                          <i className="ri-vip-crown-fill text-teal-600"></i>
                          <span className="text-teal-800 font-medium">Upgrade na {pendingUpgrade.planName}:</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="font-semibold text-teal-700">+{pendingUpgrade.price} Kč</span>
                          <button
                            onClick={() => {
                              setPendingUpgrade(null);
                              // Vrátit původní členství
                              if (originalMembership) {
                                setMembership({
                                  plan_name: originalMembership.plan_name,
                                  discount: originalMembership.discount,
                                  membership_code: originalMembership.membership_code,
                                });
                                setMembershipDiscount(originalMembership.discount);
                                setMembershipStatus({
                                  type: 'success',
                                  message: `Členství ${originalMembership.plan_name} obnoveno. Sleva ${originalMembership.discount}% je aktivní.`,
                                });
                                setOriginalMembership(null);
                              } else {
                                setMembership(null);
                                setMembershipDiscount(0);
                                setMembershipStatus(null);
                              }
                              localStorage.removeItem('cartMembershipUpgrade');
                            }}
                            className="w-5 h-5 flex items-center justify-center rounded-full bg-red-100 hover:bg-red-200 text-red-600 transition-colors cursor-pointer"
                            title="Odebrat upgrade"
                          >
                            <i className="ri-close-line text-sm"></i>
                          </button>
                        </div>
                      </div>
                    )}

                    {/* Rozpad cen: kusy se slevou vs. plná cena */}
                    {(membership || membershipDiscount > 0) && getTotalItems() > 0 && (
                      <div className="bg-gray-50 -mx-2 px-2 py-2 rounded-lg border border-gray-200 space-y-1">
                        <p className="text-xs font-semibold text-gray-600 flex items-center gap-1 mb-1">
                          <i className="ri-pie-chart-line text-teal-600"></i>
                          Rozpad cen položek:
                        </p>
                        {getDiscountedPiecesUsed() > 0 && (
                          <div className="flex justify-between items-center text-xs sm:text-sm">
                            <span className="text-teal-700 flex items-center gap-1">
                              <i className="ri-check-line text-teal-500"></i>
                              {getDiscountedPiecesUsed()} ks se slevou {getDiscountPercent()}%
                            </span>
                            <span className="font-semibold text-teal-700">
                              {(() => {
                                let total = 0;
                                let processed = 0;
                                const limit = getMembershipPiecesLimit();
                                for (const item of cartItems) {
                                  for (let i = 0; i < item.quantity; i++) {
                                    processed++;
                                    if (processed <= limit) {
                                      total += getItemPrice(item.basePrice);
                                    }
                                  }
                                }
                                return total;
                              })()} Kč
                            </span>
                          </div>
                        )}
                        {getOverLimitPieces() > 0 && (
                          <div className="flex justify-between items-center text-xs sm:text-sm">
                            <span className="text-amber-700 flex items-center gap-1">
                              <i className="ri-price-tag-3-line text-amber-500"></i>
                              {getOverLimitPieces()} ks za plnou cenu
                            </span>
                            <span className="font-semibold text-amber-700">
                              {(() => {
                                let total = 0;
                                let processed = 0;
                                const limit = getMembershipPiecesLimit();
                                for (const item of cartItems) {
                                  for (let i = 0; i < item.quantity; i++) {
                                    processed++;
                                    if (processed > limit) {
                                      total += item.basePrice;
                                    }
                                  }
                                }
                                return total;
                              })()} Kč
                            </span>
                          </div>
                        )}
                      </div>
                    )}
                    
                    <div className="flex justify-between items-center pt-3 sm:pt-4 border-t-2 border-gray-200">
                      <span className="text-lg sm:text-xl font-bold text-gray-900">Celkem:</span>
                      <span className="text-xl sm:text-2xl font-bold text-teal-600">{getTotalPrice()} Kč</span>
                    </div>
                  </div>
                </div>

                {/* Feedback */}
                {submitError && (
                  <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-4 text-sm">
                    <i className="ri-error-warning-line mr-1"></i>
                    {submitError}
                  </div>
                )}
                {submitSuccess && (
                  <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg mb-4 text-sm">
                    <i className="ri-checkbox-circle-line mr-1"></i>
                    Objednávka odeslána!
                  </div>
                )}

                {/* Submit button */}
                <button
                  onClick={handleSubmitOrder}
                  disabled={isSubmitting || cartItems.length === 0}
                  className="w-full bg-gradient-to-r from-teal-600 to-emerald-500 hover:from-teal-700 hover:to-emerald-600 text-white py-3 sm:py-4 rounded-xl text-base sm:text-lg font-bold transition-all shadow-xl hover:shadow-2xl disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-checkbox-circle-fill text-lg sm:text-xl"></i>
                  {isSubmitting ? 'Odesílám...' : 'Objednat se závazkem'}
                </button>
                <p className="text-xs text-gray-500 text-center mt-2 sm:mt-3">
                  <i className="ri-shield-check-line mr-1"></i>
                  Kliknutím potvrzujete závaznou objednávku
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </>
  );
}
